// Facebook Pixel (Meta Pixel) — client-side ad-tracking + conversion attribution.
//
// The Pixel ID is read from REACT_APP_FB_PIXEL_ID at build time. If unset,
// every export in this module is a silent no-op so local dev + preview envs
// don't fire real Pixel events into your Ads Manager.
//
// Docs: https://developers.facebook.com/docs/meta-pixel/get-started

const PIXEL_ID = (process.env.REACT_APP_FB_PIXEL_ID || "").trim();

let initialized = false;

function fbq(...args) {
  if (typeof window === "undefined") return;
  if (typeof window.fbq !== "function") return;
  try {
    window.fbq(...args);
  } catch {
    // Ad blocker / CSP — silently swallow.
  }
}

/**
 * Inject the standard Meta Pixel snippet and fire the initial PageView.
 * Idempotent: repeat calls are no-ops after the first success.
 */
export function initPixel() {
  if (initialized || !PIXEL_ID) return;
  if (typeof window === "undefined" || typeof document === "undefined") return;
  // Standard Meta Pixel bootstrap (verbatim from developers.facebook.com,
  // wrapped so we can no-op cleanly when PIXEL_ID is empty).
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", PIXEL_ID);
  window.fbq("track", "PageView");
  initialized = true;
}

/** Fire a PageView — call on every SPA route change after the initial load. */
export function trackPageView() {
  if (!PIXEL_ID) return;
  if (!initialized) initPixel();
  fbq("track", "PageView");
}

/**
 * Fire the standard `Lead` event — call once immediately after a successful
 * user registration. Facebook uses `Lead` for signup-conversion campaigns.
 */
export function trackLead(extra = {}) {
  if (!PIXEL_ID) return;
  fbq("track", "Lead", extra);
}

/**
 * Fire the standard `Purchase` event — call once payment is confirmed as paid.
 * `value` = dollars (number). `currency` defaults to USD.
 */
export function trackPurchase({ value, currency = "USD", contentName, contentType } = {}) {
  if (!PIXEL_ID) return;
  const params = { currency };
  if (typeof value === "number" && !Number.isNaN(value)) params.value = value;
  if (contentName) params.content_name = contentName;
  if (contentType) params.content_type = contentType;
  fbq("track", "Purchase", params);
}

/** Fire the standard `InitiateCheckout` event — call when user hits "Subscribe". */
export function trackInitiateCheckout({ value, currency = "USD" } = {}) {
  if (!PIXEL_ID) return;
  const params = { currency };
  if (typeof value === "number" && !Number.isNaN(value)) params.value = value;
  fbq("track", "InitiateCheckout", params);
}

export const pixelConfigured = Boolean(PIXEL_ID);
